import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type {
  SistemaComponent,
  SistemaComponentWithChannels,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  defaultSistemaComponentFormData,
  validateSistemaComponentForm,
  type SistemaComponentFormData,
} from './sistemaArchitectureForm.schema'
import {
  SISTEMA_CAT_OPTIONS,
  SISTEMA_FUNCTION_TYPE_OPTIONS,
} from './sistemaFieldOptions'

type SistemaComponentSheetProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (payload: SistemaComponentFormData) => void
  component?: SistemaComponent | SistemaComponentWithChannels | null
  isSaving?: boolean
}

function toFormData(
  component?: SistemaComponent | SistemaComponentWithChannels | null
): SistemaComponentFormData {
  if (component == null) {
    return defaultSistemaComponentFormData
  }
  return {
    name: component.name,
    cat: component.cat ?? '',
    functiontypes: component.functiontypes ?? '',
    missiontime: component.missiontime ?? '',
  }
}

export function SistemaComponentSheet({
  isOpen,
  onClose,
  onSave,
  component,
  isSaving = false,
}: SistemaComponentSheetProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const isEdit = component != null
  const [formData, setFormData] = useState<SistemaComponentFormData>(() =>
    toFormData(component)
  )

  useEffect(() => {
    if (isOpen) {
      setFormData(toFormData(component))
    }
  }, [isOpen, component])

  const isValid = useMemo(() => {
    return validateSistemaComponentForm(formData).success
  }, [formData])
  const catItems = useMemo(
    () =>
      SISTEMA_CAT_OPTIONS.map((option) => ({
        value: option,
        label: t(`safetyFunctionsPage.enums.cat.${option}`),
      })),
    [t]
  )
  const functionTypeItems = useMemo(
    () =>
      SISTEMA_FUNCTION_TYPE_OPTIONS.map((option) => ({
        value: option,
        label: t(`safetyFunctionsPage.enums.functiontypes.${option}`),
      })),
    [t]
  )

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <SheetContent
        side='right'
        className='flex h-full w-full flex-col overflow-hidden sm:max-w-md'
      >
        <SheetHeader className='shrink-0'>
          <SheetTitle>
            {isEdit
              ? t('safetyFunctionsPage.architecture.editComponent')
              : t('safetyFunctionsPage.architecture.addComponent')}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className='min-h-0 flex-1'>
          <div className='space-y-4 px-4 pb-4'>
            <div className='space-y-2'>
              <Label htmlFor='component-name'>
                {t('safetyFunctionsPage.architecture.name')}{' '}
                <span className='text-destructive'>*</span>
              </Label>
              <Input
                id='component-name'
                value={formData.name}
                onChange={(event) =>
                  setFormData({ ...formData, name: event.target.value })
                }
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='component-cat'>
                {t('safetyFunctionsPage.srsFields.cat')}
              </Label>
              <Select
                value={formData.cat || undefined}
                onValueChange={(value) => {
                  if (value) {
                    setFormData({ ...formData, cat: value })
                  }
                }}
                items={catItems}
              >
                <SelectTrigger id='component-cat' className='w-full'>
                  <SelectValue
                    placeholder={t(
                      'safetyFunctionsPage.architecture.selectOption'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {SISTEMA_CAT_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`safetyFunctionsPage.enums.cat.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='component-functiontypes'>
                {t('safetyFunctionsPage.srsFields.functiontypes')}
              </Label>
              <Select
                value={formData.functiontypes || undefined}
                onValueChange={(value) => {
                  if (value) {
                    setFormData({ ...formData, functiontypes: value })
                  }
                }}
                items={functionTypeItems}
              >
                <SelectTrigger id='component-functiontypes' className='w-full'>
                  <SelectValue
                    placeholder={t(
                      'safetyFunctionsPage.architecture.selectOption'
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {SISTEMA_FUNCTION_TYPE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`safetyFunctionsPage.enums.functiontypes.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='component-missiontime'>
                {t('safetyFunctionsPage.srsFields.missiontime')}
              </Label>
              <Input
                id='component-missiontime'
                value={formData.missiontime}
                onChange={(event) =>
                  setFormData({ ...formData, missiontime: event.target.value })
                }
              />
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className='shrink-0'>
          <Button
            onClick={() => {
              if (isValid) {
                onSave(formData)
              }
            }}
            disabled={!isValid || isSaving}
          >
            {isSaving ? <Loader2 className='mr-2 size-4 animate-spin' /> : null}
            {t('common:actions.save')}
          </Button>
          <Button variant='outline' onClick={onClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
